import React from 'react'
import {
  Alert,
  Box,
  Container,
  FormControl,
  FormLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
  Button,
} from '@mui/material'
import type { MeResponse } from '../../api/me'
import type { BillingEligibility } from '../../api/billing'
import { createCheckout, createPortal, getStationEligibility } from '../../api/billing'
import type { PricingPrice } from '../../api/pricing'
import { usePricingData } from '../../components/pricing/usePricingData'

export type YoutubeCreditsViewProps = {
  variant: 'public' | 'app'
  me?: MeResponse | null
  isLoggedIn: boolean
}

const YoutubeCreditsView: React.FC<YoutubeCreditsViewProps> = ({ variant, isLoggedIn, me }) => {
  const { currency, setCurrency, supportedCurrencies, pricing, loading, error } = usePricingData('credits')
  const currencyOptions = React.useMemo(() => {
    const defaults = ['EUR', 'GBP', 'USD']
    return supportedCurrencies.length ? supportedCurrencies : defaults
  }, [supportedCurrencies])
  const [ownerType, setOwnerType] = React.useState<'user' | 'station'>('user')
  const [stationId, setStationId] = React.useState<number | null>(null)
  const [eligibility, setEligibility] = React.useState<BillingEligibility | null>(null)
  const [eligibilityError, setEligibilityError] = React.useState<string | null>(null)
  const [eligibilityLoading, setEligibilityLoading] = React.useState(false)
  const [checkoutLoading, setCheckoutLoading] = React.useState<string | null>(null)
  const [checkoutError, setCheckoutError] = React.useState<string | null>(null)
  const [portalLoading, setPortalLoading] = React.useState<'user' | 'station' | null>(null)
  const [portalError, setPortalError] = React.useState<string | null>(null)
  const [autoCheckout, setAutoCheckout] = React.useState<{
    planKey: string
    ownerType?: 'user' | 'station'
    stationId?: number
    currency?: string
  } | null>(null)

  const stationOptions = me?.stations ?? []
  const products = pricing?.products ?? []
  const creditProducts = React.useMemo(
    () => products.filter((product) => product.group === 'credits' && product.active),
    [products],
  )

  React.useEffect(() => {
    if (!me?.stations?.length) {
      setStationId(null)
      return
    }

    setStationId((prev) => (prev && me.stations.some((station) => station.id === prev) ? prev : me.stations[0].id))
  }, [me?.stations])

  React.useEffect(() => {
    if (!isLoggedIn || ownerType !== 'station' || !stationId) {
      setEligibility(null)
      setEligibilityError(null)
      setEligibilityLoading(false)
      return
    }

    let cancelled = false
    setEligibilityLoading(true)
    setEligibilityError(null)

    getStationEligibility(stationId)
      .then((response) => {
        if (cancelled) {
          return
        }
        setEligibility(response)
      })
      .catch((err) => {
        if (cancelled) {
          return
        }
        const message = err instanceof Error ? err.message : 'Unable to check station eligibility.'
        setEligibilityError(message)
      })
      .finally(() => {
        if (!cancelled) {
          setEligibilityLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [isLoggedIn, ownerType, stationId])

  const formatPrice = React.useCallback((amount: number | null, currencyCode: string) => {
    if (amount === null || Number.isNaN(amount)) {
      return 'Contact us'
    }

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        maximumFractionDigits: 2,
      }).format(amount / 100)
    } catch {
      return `${currencyCode} ${(amount / 100).toFixed(2)}`
    }
  }, [])

  const intervalLabel = React.useCallback((price?: PricingPrice | null) => {
    if (!price?.interval) {
      return ''
    }

    if (price.interval_count && price.interval_count > 1) {
      return `every ${price.interval_count} ${price.interval}s`
    }

    return `per ${price.interval}`
  }, [])

  const handleCheckout = React.useCallback(
    async (planKey: string) => {
      if (!isLoggedIn) {
        const returnUrl = new URL(window.location.href)
        returnUrl.searchParams.set('rpa_plan', planKey)
        returnUrl.searchParams.set('rpa_currency', currency)
        returnUrl.searchParams.set('rpa_owner_type', ownerType)
        returnUrl.searchParams.set('rpa_autocheckout', '1')
        if (stationId) {
          returnUrl.searchParams.set('rpa_station_id', String(stationId))
        }
        const loginUrl = new URL('/rpa-login', window.location.origin)
        loginUrl.searchParams.set('redirect_to', returnUrl.toString())
        window.location.assign(loginUrl.toString())
        return
      }

      setCheckoutError(null)
      setCheckoutLoading(planKey)

      try {
        const payload = {
          plan: planKey,
          currency,
          owner_type: ownerType,
          station_id: ownerType === 'station' ? stationId ?? undefined : undefined,
        }
        const response = await createCheckout(payload)
        if (response.url) {
          window.location.assign(response.url)
          return
        }
        throw new Error('Stripe checkout did not return a URL.')
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unable to start checkout.'
        setCheckoutError(message)
      } finally {
        setCheckoutLoading(null)
      }
    },
    [currency, isLoggedIn, ownerType, stationId],
  )

  const handleManageBilling = React.useCallback(
    async () => {
      if (!isLoggedIn) {
        window.location.assign('/rpa-login')
        return
      }

      setPortalError(null)
      setPortalLoading(ownerType)

      try {
        const response = await createPortal({
          customer_type: ownerType,
          station_id: ownerType === 'station' ? stationId ?? undefined : undefined,
        })
        if (response.url) {
          window.location.assign(response.url)
          return
        }
        throw new Error('Billing portal did not return a URL.')
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unable to open billing portal.'
        setPortalError(message)
      } finally {
        setPortalLoading(null)
      }
    },
    [isLoggedIn, ownerType, stationId],
  )

  const checkoutSuccess = React.useMemo(() => {
    if (typeof window === 'undefined') {
      return false
    }
    const params = new URLSearchParams(window.location.search)
    return params.get('checkout') === 'success'
  }, [])

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const params = new URLSearchParams(window.location.search)
    const planKey = params.get('rpa_plan')
    const auto = params.get('rpa_autocheckout') === '1'

    if (!planKey || !auto) {
      return
    }

    const ownerParam = params.get('rpa_owner_type')
    const stationParam = params.get('rpa_station_id')
    const currencyParam = params.get('rpa_currency')

    const station = stationParam ? Number.parseInt(stationParam, 10) : undefined
    const normalizedStation = station && !Number.isNaN(station) ? station : undefined

    setAutoCheckout({
      planKey,
      ownerType: ownerParam === 'station' ? 'station' : 'user',
      stationId: normalizedStation,
      currency: currencyParam ? currencyParam.toUpperCase() : undefined,
    })
  }, [])

  React.useEffect(() => {
    if (!autoCheckout) {
      return
    }

    if (autoCheckout.currency && autoCheckout.currency !== currency) {
      setCurrency(autoCheckout.currency)
      return
    }

    if (autoCheckout.ownerType && autoCheckout.ownerType !== ownerType) {
      setOwnerType(autoCheckout.ownerType)
    }

    if (autoCheckout.stationId && autoCheckout.stationId !== stationId) {
      setStationId(autoCheckout.stationId)
    }

    if (!isLoggedIn || !pricing) {
      return
    }

    if (autoCheckout.ownerType === 'station') {
      if (!stationId || eligibilityLoading) {
        return
      }
      if (eligibility && !eligibility.is_admin) {
        return
      }
    }

    void handleCheckout(autoCheckout.planKey)
    setAutoCheckout(null)
  }, [
    autoCheckout,
    currency,
    eligibility,
    eligibilityLoading,
    handleCheckout,
    isLoggedIn,
    ownerType,
    pricing,
    setCurrency,
    stationId,
  ])

  return (
    <Box
      sx={{
        minHeight: variant === 'public' ? '100vh' : 'auto',
        backgroundColor: variant === 'public' ? 'background.default' : 'transparent',
        py: variant === 'public' ? { xs: 6, md: 8 } : 0,
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={3}>
          <Stack spacing={1}>
            <Typography variant={variant === 'public' ? 'h3' : 'h4'} component="h1">
              YouTube credits
            </Typography>
            <Typography color="text.secondary">
              Buy credits for audio and video downloads.
            </Typography>
          </Stack>

          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Stack spacing={2}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <FormLabel>Currency</FormLabel>
                <Select
                  value={currency}
                  onChange={(event) => setCurrency(event.target.value)}
                  disabled={currencyOptions.length <= 1}
                >
                  {currencyOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {isLoggedIn ? (
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <FormLabel>Apply credits to</FormLabel>
                  <Select
                    value={ownerType}
                    onChange={(event) => setOwnerType(event.target.value === 'station' ? 'station' : 'user')}
                  >
                    <MenuItem value="user">My account</MenuItem>
                    <MenuItem value="station" disabled={stationOptions.length === 0}>
                      Station
                    </MenuItem>
                  </Select>
                </FormControl>
              ) : null}
              {isLoggedIn && ownerType === 'station' ? (
                <FormControl size="small" sx={{ minWidth: 240 }}>
                  <FormLabel>Select station</FormLabel>
                  <Select
                    value={stationId ?? ''}
                    onChange={(event) => setStationId(Number(event.target.value))}
                    displayEmpty
                  >
                    <MenuItem value="">
                      {stationOptions.length ? 'Select station' : 'No stations available'}
                    </MenuItem>
                    {stationOptions.map((station) => (
                      <MenuItem key={station.id} value={station.id}>
                        {station.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
              <Typography color="text.secondary">
                {isLoggedIn
                  ? 'Choose a credit bundle to continue.'
                  : 'Continue to checkout to sign in and finish purchase.'}
              </Typography>
              {loading ? <Typography color="text.secondary">Loading pricing...</Typography> : null}
              {error ? <Alert severity="error">{error}</Alert> : null}
              {eligibilityLoading ? (
                <Typography color="text.secondary">Checking station permissions...</Typography>
              ) : null}
              {eligibilityError ? <Alert severity="error">{eligibilityError}</Alert> : null}
              {ownerType === 'station' && eligibility && !eligibility.is_admin ? (
                <Alert severity="warning">
                  Only station admins can purchase station credit packs.
                </Alert>
              ) : null}
              {checkoutError ? <Alert severity="error">{checkoutError}</Alert> : null}
              {portalError ? <Alert severity="error">{portalError}</Alert> : null}
              {checkoutSuccess ? (
                <Alert severity="success">Checkout completed. Credits will update shortly.</Alert>
              ) : null}
              {isLoggedIn ? (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleManageBilling}
                  disabled={
                    portalLoading !== null ||
                    (ownerType === 'station' && (!stationId || (eligibility ? !eligibility.is_admin : true)))
                  }
                >
                  Manage billing
                </Button>
              ) : null}
              {pricing ? (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                    gap: 2,
                  }}
                >
                  {creditProducts.length === 0 ? (
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                      <Typography color="text.secondary">No credit products are available.</Typography>
                    </Paper>
                  ) : null}
                  {creditProducts.map((product) => {
                    const price = product.price
                    const isAvailable = Boolean(product.available && price)
                    const bullets = product.bullets ?? []
                    return (
                      <Paper key={product.key} variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                        <Stack spacing={1.5}>
                          <Typography variant="h6">{product.label || 'Credits'}</Typography>
                          <Typography variant="h4" component="div">
                            {isAvailable
                              ? formatPrice(price?.unit_amount ?? null, price?.currency || currency)
                              : 'Not available'}{' '}
                            <Typography component="span" color="text.secondary" variant="body2">
                              {intervalLabel(price)}
                            </Typography>
                          </Typography>
                          {!isAvailable ? (
                            <Typography color="warning.main">Not available in {currency}</Typography>
                          ) : null}
                          {product.credit_amount ? (
                            <Typography color="text.secondary">
                              {product.credit_amount} credits
                            </Typography>
                          ) : null}
                          {product.description ? (
                            <Typography color="text.secondary">{product.description}</Typography>
                          ) : null}
                          {bullets.length ? (
                            <Stack spacing={0.5}>
                              {bullets.map((bullet) => (
                                <Typography key={bullet} variant="body2" color="text.secondary">
                                  - {bullet}
                                </Typography>
                              ))}
                            </Stack>
                          ) : null}
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleCheckout(product.key)}
                            disabled={
                              checkoutLoading === product.key ||
                              !isAvailable ||
                              (isLoggedIn &&
                                ownerType === 'station' &&
                                (!stationId || (eligibility ? !eligibility.is_admin : true)))
                            }
                          >
                            Continue to checkout
                          </Button>
                        </Stack>
                      </Paper>
                    )
                  })}
                </Box>
              ) : null}
            </Stack>
          </Paper>
        </Stack>
      </Container>
    </Box>
  )
}

export default YoutubeCreditsView
